import React, { FC, useState } from "react";
import { Modal, Button, Select, DatePicker, notification, Form } from "antd";
import { useExportCSVMutation } from "shared/api";
import { Dayjs } from "dayjs";

interface ExportCSVModalProps {
  isOpen: boolean;
  onClose: () => void;
  machines: { label: string; value: number }[];
}

const ExportCSVModal: FC<ExportCSVModalProps> = ({
  isOpen,
  onClose,
  machines,
}) => {
  const [selectedMachine, setSelectedMachine] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const [exportCSV, { isLoading }] = useExportCSVMutation();

  const handleClose = () => {
    setSelectedMachine(null);
    setStartDate(null);
    setEndDate(null);
    onClose();
  };

  const handleExport = async () => {
    if (!selectedMachine || !startDate || !endDate) {
      notification.error({
        message: "Missing fields",
        description: "Please select machine, start date, and end date",
      });
      return;
    }

    try {
      const blob = await exportCSV({
        machine_id: selectedMachine,
        start_dt: startDate.format("YYYY-MM-DDT00:00:00"),
        end_dt: endDate.format("YYYY-MM-DDT23:59:59"),
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `production_analysis_${selectedMachine}_${startDate.format(
        "YYYY-MM-DD"
      )}_${endDate.format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notification.success({
        message: "Export successful",
        description: "CSV file has been downloaded",
      });

      handleClose();
    } catch (error) {
      notification.error({
        message: "Export failed",
        description: "Failed to export CSV file",
      });
    }
  };

  return (
    <Modal
      title="Export CSV"
      open={isOpen}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="export"
          type="primary"
          loading={isLoading}
          onClick={handleExport}
          disabled={!selectedMachine || !startDate || !endDate}
        >
          Export
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item label="Machine" required>
          <Select
            placeholder="Select machine"
            value={selectedMachine}
            onChange={setSelectedMachine}
            options={machines}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item label="Start Date" required>
          <DatePicker
            className="w-full"
            value={startDate}
            onChange={setStartDate}
            format="YYYY-MM-DD"
          />
        </Form.Item>

        <Form.Item label="End Date" required>
          <DatePicker
            className="w-full"
            value={endDate}
            onChange={setEndDate}
            format="YYYY-MM-DD"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export { ExportCSVModal };
